import { Component, Inject, OnInit, signal } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import {
    EmployeeGroupChangeCompanyInformationDTO,
    EmployeeGroupChangeRequestDTO,
    EmployeeOverviewView,
    ScheduledGroupChangeView,
    ScheduleEmployeeGroupChangeRequest,
    UpdateEmployeeGroupChangeDateRequest
} from '@iota/inh-core-v1-management-client';
import { IotaModalSize, MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@iota/iota-ui-components';
import { Store } from '@ngrx/store';
import { map } from 'rxjs';
import {
    employeeGroupChangeRequestById,
    employeeGroupChangesActionError,
    employeeGroupChangesActionLoading
} from 'src/app/store/reducer/employee-group-change.reducer';
import { selectedCompanyEmployeeGroupChangeInformation, selectedCompanyId, selectedCompanyOfficeIdToNameMap } from 'src/app/store/store';
import { DateValidator } from '../../validators/date-validator';
import moment, { Moment } from 'moment';
import { ConfirmationDialogData, DialogConfirmationComponent } from '../dialog-confirmation/dialog-confirmation.component';
import {
    CancelEmployeeGroupChange,
    LoadCompanyEmployeeGroupChangeInformation,
    ScheduleEmployeeGroupChange,
    UpdateEmployeeGroupChangeDate
} from 'src/app/store/actions/employee-group-change.actions';
import { getValueFromStore, waitForBooleanValue } from '../../utils';

export interface EmployeeGroupChangeDialogData {
    employeeGroupChangeId?: string;
    employeeGroupChangeRequest?: ScheduledGroupChangeView;
    companyEmployeeId?: string;
    employee?: EmployeeOverviewView;
}

@Component({
    standalone: false,
    selector: 'app-employee-group-change-dialog',
    templateUrl: './employee-group-change-dialog.component.html',
    styleUrls: ['./employee-group-change-dialog.component.scss']
})
export class EmployeeGroupChangeDialogComponent implements OnInit {
    public modalOptions = {
        size: IotaModalSize.SIZE_3XL
    };

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: EmployeeGroupChangeDialogData,
        private dialogRef: MatDialogRef<EmployeeGroupChangeDialogComponent>,
        private store: Store,
        private formBuilder: FormBuilder,
        private dialog: MatDialog
    ) {}

    private readonly selectedGroupChangeRequestState = signal<EmployeeGroupChangeRequestDTO>(undefined);
    readonly selectedGroupChangeRequest = this.selectedGroupChangeRequestState.asReadonly();

    cancelGroupChangePressed = false;
    savePressed = false;
    readonly informationLoading = signal(true);

    actionLoading$ = this.store.select(employeeGroupChangesActionLoading);
    actionError$ = this.store.select(employeeGroupChangesActionError);
    officeIdToNameMap$ = this.store.select(selectedCompanyOfficeIdToNameMap);

    earliestPossibleDate$ = this.store
        .select(selectedCompanyEmployeeGroupChangeInformation)
        .pipe(map((info) => this.getEarliestChangeDate(info).format('YYYY-MM-DD')));
    possibleEmployeeGroupsToChange$ = this.store
        .select(selectedCompanyEmployeeGroupChangeInformation)
        .pipe(map((info) => info?.employeeGroupsThatAreAllowedToChange));

    form = this.formBuilder.group({
        newEmployeeGroup: [null as string, [Validators.required]],
        changeDate: [null as string, [Validators.required, DateValidator.FirstOfAMonth]]
    });

    ngOnInit(): void {
        this.initDialog();
    }

    async initDialog() {
        const companyId = await getValueFromStore(this.store, selectedCompanyId);
        this.store.dispatch(LoadCompanyEmployeeGroupChangeInformation({ companyId }));

        if (this.data?.employeeGroupChangeId) {
            // The employee group cannot be edited after the request is scheduled
            this.form.get('newEmployeeGroup').disable();

            const groupChangeRequest =
                this.data.employeeGroupChangeRequest ??
                (await getValueFromStore(this.store, employeeGroupChangeRequestById(this.data.employeeGroupChangeId)));
            this.selectedGroupChangeRequestState.set(groupChangeRequest);
            this.form.patchValue({
                newEmployeeGroup: groupChangeRequest?.newEmployeeGroup,
                changeDate: groupChangeRequest?.changeDate
            });

            if (!groupChangeRequest?.canBeModified) {
                this.form.disable();
            }
        }

        await waitForBooleanValue(this.store, employeeGroupChangesActionLoading, false);
        const info = await getValueFromStore(this.store, selectedCompanyEmployeeGroupChangeInformation);
        this.form.get('changeDate').addValidators(DateValidator.MinDate(this.getEarliestChangeDate(info)));
        this.informationLoading.set(false);
    }

    private getEarliestChangeDate(info: EmployeeGroupChangeCompanyInformationDTO | undefined): Moment {
        return info?.earliestChangeDate ? moment(info.earliestChangeDate) : moment().add(1, 'month').startOf('month');
    }

    cancelGroupChange() {
        const dialogData: ConfirmationDialogData = {
            title: $localize`Cancel group change`,
            text: $localize`Are you sure you want to cancel this group change?`,
            cancelButtonClass: 'btn-outline',
            confirmButtonStyle: 'primary'
        };

        this.dialog
            .open(DialogConfirmationComponent, {
                data: dialogData
            })
            .afterClosed()
            .subscribe(async (confirmed) => {
                if (confirmed) {
                    this.savePressed = false;
                    this.cancelGroupChangePressed = true;
                    this.store.dispatch(CancelEmployeeGroupChange({ id: this.data?.employeeGroupChangeId }));
                    this.waitForActionAndCloseDialogOnSuccess();
                }
            });
    }

    closeDialog() {
        this.dialogRef.close();
    }

    get showCancelButton() {
        return !!this.data?.employeeGroupChangeId && this.selectedGroupChangeRequest()?.canBeModified;
    }

    get isCancelButtonDisabled() {
        return this.informationLoading() || !this.selectedGroupChangeRequest()?.canBeModified;
    }

    get showSaveButton() {
        return !this.data?.employeeGroupChangeId || this.selectedGroupChangeRequest()?.canBeModified;
    }

    get isSaveButtonDisabled() {
        const formInvalid = !this.form?.valid;
        const cannotBeModifiedAnymore = !!this.data?.employeeGroupChangeId && !this.selectedGroupChangeRequest()?.canBeModified;
        return this.informationLoading() || formInvalid || cannotBeModifiedAnymore;
    }

    get isScheduledAndCannotBeModified() {
        const groupChangeRequest = this.selectedGroupChangeRequest();
        return (
            !!groupChangeRequest &&
            groupChangeRequest.status === EmployeeGroupChangeRequestDTO.StatusEnum.SCHEDULED &&
            !groupChangeRequest.canBeModified
        );
    }

    async save() {
        this.cancelGroupChangePressed = false;
        this.savePressed = true;

        if (!this.data?.employeeGroupChangeId) {
            const companyId = await getValueFromStore(this.store, selectedCompanyId);
            const newGroupChange: ScheduleEmployeeGroupChangeRequest = {
                ...this.form.getRawValue(),
                companyId,
                companyEmployeeId: this.data?.companyEmployeeId
            };
            this.store.dispatch(ScheduleEmployeeGroupChange({ request: newGroupChange }));
        } else {
            const updatedGroupChange: UpdateEmployeeGroupChangeDateRequest = {
                newChangeDate: this.form.get('changeDate').value
            };
            this.store.dispatch(UpdateEmployeeGroupChangeDate({ id: this.data.employeeGroupChangeId, request: updatedGroupChange }));
        }

        this.waitForActionAndCloseDialogOnSuccess();
    }

    async waitForActionAndCloseDialogOnSuccess() {
        await waitForBooleanValue(this.store, employeeGroupChangesActionLoading, false);
        const actionError = await getValueFromStore(this.store, employeeGroupChangesActionError);

        if (!actionError) {
            this.closeDialog();
        }
    }
}
